import { Card } from 'primereact/card';
import styles from '../AnalysisWizardSteps.module.css';
import { DocumentSelector } from '../../DocumentSelector/DocumentSelector';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { AnalysisCreateRequest } from '@/api/domains/analyses';
import { DocumentSummary, useSimilarDocuments } from '@/api/domains/documents';

interface StepSelectInternalProps {
  formData: Omit<AnalysisCreateRequest, 'user_id'>;
  setFormData: (data: Omit<AnalysisCreateRequest, 'user_id'>) => void;
  documents: DocumentSummary[];
  handleBack: () => void;
  handleNext: () => void;
  disableSimilaritySearch: boolean;
}

const StepSelectInternal = ({
  formData,
  setFormData,
  documents,
  handleNext,
  handleBack,
  disableSimilaritySearch
}: StepSelectInternalProps) => {
  const { data: similarDocs, isLoading: loadingSimilar } = useSimilarDocuments(
    formData.document_1_id || null,
    {
      top_k: 5,
      threshold: 0.4
    },
    {
      enabled: !disableSimilaritySearch
    }
  );

  const canProceedStep2 = formData.document_2_id !== '';

  return (
    <Card className={styles.stepCard}>
      <div className={styles.stepHeader}>
        <h2 className={styles.stepTitle}>Choose Internal Document</h2>
        <p className={styles.stepDescription}>
          Select the internal document to compare against the external document.
        </p>
      </div>

      {loadingSimilar && (
        <div className={styles.loadingContainer}>
          <ProgressSpinner
            style={{ width: '50px', height: '50px' }}
            strokeWidth="4"
          />
          <p>Finding similar documents...</p>
        </div>
      )}

      {!loadingSimilar && (
        <DocumentSelector
          documents={documents}
          selectedDocumentId={formData.document_2_id}
          onSelect={(sourceUrl) =>
            setFormData({ ...formData, document_2_id: sourceUrl })
          }
          excludeDocumentId={formData.document_1_id}
          title={
            similarDocs && similarDocs.length > 0
              ? 'Recommended Internal Documents'
              : 'Available Internal Documents'
          }
          description={
            similarDocs && similarDocs.length > 0
              ? 'Internal documents sorted by similarity to the external document'
              : 'Select an internal document to compare'
          }
          showSimilarityScores={similarDocs && similarDocs.length > 0}
          similarityScores={
            similarDocs
              ? new Map(
                  similarDocs.map((d) => [d.source_url, d.similarity_score])
                )
              : undefined
          }
          emptyMessage="No documents available for comparison"
        />
      )}

      <div className={styles.stepActions}>
        <Button
          label="Back"
          icon="pi pi-arrow-left"
          onClick={handleBack}
          className={styles.textButton}
        />
        <Button
          label="Next"
          icon="pi pi-arrow-right"
          iconPos="right"
          onClick={handleNext}
          disabled={!canProceedStep2}
          className={styles.primaryButton}
        />
      </div>
    </Card>
  );
};

export default StepSelectInternal;
